/** Admin → Location Service → Area offers: target coupons at zone types, zones, pincodes, villages, cities or
 * districts; limit products to zones; refresh product ratings. Coupons themselves are created on Admin → Coupons. */
import { useEffect, useState } from "react";
import { MapPin, RefreshCcw, Target } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { ZONE_TYPE_LABEL, useLse } from "../../../lib/lse";
import { Btn, Empty, Field, Loading, Modal, PageHead, Pill, errText, inputCls } from "./LseUI";

const splitList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function LseOffers() {
    const { has } = useLse();
    return (
        <div data-testid="lse-offers" className="space-y-8">
            {has("zone_offers") && <CouponTargeting />}
            {has("zone_products") && <ProductZones />}
            {has("rating_aggregation") && <Ratings />}
        </div>
    );
}

function CouponTargeting() {
    const [rows, setRows] = useState(null);
    const [zones, setZones] = useState([]);
    const [villages, setVillages] = useState([]);
    const [edit, setEdit] = useState(null);
    const load = () => api.get("/admin/lse/coupons").then(({ data }) => setRows(data.coupons || [])).catch(() => setRows([]));
    useEffect(() => {
        load();
        api.get("/admin/zones").then(({ data }) => setZones((data || []).filter((z) => z.status !== "DELETED"))).catch(() => {});
        api.get("/admin/lse/villages").then(({ data }) => setVillages(data.villages || [])).catch(() => {});
    }, []);
    const summary = (t) => {
        if (!t) return "Everyone";
        const parts = [];
        if (t.zone_types?.length) parts.push(t.zone_types.map((z) => ZONE_TYPE_LABEL[z]).join("/"));
        if (t.zone_ids?.length) parts.push(`${t.zone_ids.length} zone(s)`);
        if (t.village_ids?.length) parts.push(`${t.village_ids.length} village(s)`);
        if (t.pincodes?.length) parts.push(`pincodes ${t.pincodes.join(", ")}`);
        if (t.cities?.length) parts.push(t.cities.join(", "));
        if (t.districts?.length) parts.push(`districts ${t.districts.join(", ")}`);
        return parts.join(" · ");
    };
    const save = async () => {
        const body = { zone_types: edit.zone_types, zone_ids: edit.zone_ids, village_ids: edit.village_ids, pincodes: splitList(edit.pincodes), cities: splitList(edit.cities), districts: splitList(edit.districts) };
        try { await api.put(`/admin/lse/coupons/${edit.id}/targeting`, body); toast.success("Targeting saved"); setEdit(null); load(); }
        catch (e) { toast.error(errText(e)); }
    };
    const toggleIn = (k, v) => setEdit((x) => ({ ...x, [k]: x[k].includes(v) ? x[k].filter((y) => y !== v) : [...x[k], v] }));
    return (
        <section>
            <PageHead title="Area-targeted offers" sub="Make a coupon apply only in chosen areas. A targeted coupon is hidden and refused everywhere else; untargeted coupons work as before." />
            {!rows && <Loading />}
            {rows?.length === 0 && <Empty>No coupons yet. Create them on Admin → Coupons.</Empty>}
            <div className="mt-3 bg-card border border-border rounded-2xl divide-y divide-border overflow-hidden">
                {(rows || []).map((c) => (
                    <div key={c.id} className="px-4 py-3 flex items-center gap-3" data-testid={`lse-offer-${c.code}`}>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm">{c.code} <span className="text-muted-foreground font-normal">· {c.title}</span></p>
                            <p className="text-xs text-muted-foreground truncate"><Target size={11} className="inline" /> {summary(c.lse_targeting)}</p>
                        </div>
                        {c.status !== "ACTIVE" && <Pill>{c.status?.toLowerCase()}</Pill>}
                        <Btn tone="ghost" className="!px-3" onClick={() => setEdit({ id: c.id, code: c.code, zone_types: c.lse_targeting?.zone_types || [], zone_ids: c.lse_targeting?.zone_ids || [], village_ids: c.lse_targeting?.village_ids || [], pincodes: (c.lse_targeting?.pincodes || []).join(", "), cities: (c.lse_targeting?.cities || []).join(", "), districts: (c.lse_targeting?.districts || []).join(", ") })}>Target</Btn>
                    </div>
                ))}
            </div>
            {edit && (
                <Modal title={`Where ${edit.code} applies`} onClose={() => setEdit(null)} footer={<><Btn tone="ghost" onClick={() => setEdit(null)}>Cancel</Btn><Btn onClick={save} data-testid="lse-offer-save">Save</Btn></>}>
                    <p className="text-xs text-muted-foreground">Leave everything empty for “everyone”. Every filled row must match.</p>
                    <div className="mt-3 grid gap-3">
                        <Field label="Zone types"><div className="flex flex-wrap gap-2">{["URBAN", "SEMI_URBAN", "RURAL"].map((z) => <Chip key={z} on={edit.zone_types.includes(z)} onClick={() => toggleIn("zone_types", z)}>{ZONE_TYPE_LABEL[z]}</Chip>)}</div></Field>
                        <Field label="Zones"><div className="flex flex-wrap gap-2">{zones.map((z) => <Chip key={z.id} on={edit.zone_ids.includes(z.id)} onClick={() => toggleIn("zone_ids", z.id)}>{z.name}</Chip>)}</div></Field>
                        {villages.length > 0 && <Field label="Villages"><div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">{villages.map((v) => <Chip key={v.id} on={edit.village_ids.includes(v.id)} onClick={() => toggleIn("village_ids", v.id)}>{v.name}</Chip>)}</div></Field>}
                        <Field label="Pincodes (comma separated)"><input className={inputCls} value={edit.pincodes} onChange={(e) => setEdit({ ...edit, pincodes: e.target.value })} /></Field>
                        <Field label="Cities (comma separated)"><input className={inputCls} value={edit.cities} onChange={(e) => setEdit({ ...edit, cities: e.target.value })} /></Field>
                        <Field label="Districts (comma separated)"><input className={inputCls} value={edit.districts} onChange={(e) => setEdit({ ...edit, districts: e.target.value })} /></Field>
                    </div>
                </Modal>
            )}
        </section>
    );
}

function Chip({ on, onClick, children }) {
    return <button type="button" onClick={onClick} className={`rounded-full px-3 py-1 text-xs font-semibold border ${on ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{children}</button>;
}

function ProductZones() {
    const [zones, setZones] = useState([]);
    const [restricted, setRestricted] = useState(null);
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");
    const [edit, setEdit] = useState(null);
    const load = () => api.get("/admin/lse/products/zone-restricted").then(({ data }) => setRestricted(data.products || [])).catch(() => setRestricted([]));
    useEffect(() => {
        load();
        api.get("/admin/zones").then(({ data }) => setZones((data || []).filter((z) => z.status !== "DELETED"))).catch(() => {});
        api.get("/admin/products").then(({ data }) => setProducts(Array.isArray(data) ? data : data?.items || [])).catch(() => {});
    }, []);
    const save = async () => {
        try { await api.put(`/admin/lse/products/${edit.id}/zones`, { zone_ids: edit.zone_ids }); toast.success("Saved"); setEdit(null); load(); }
        catch (e) { toast.error(errText(e)); }
    };
    const matches = q ? products.filter((p) => p.name?.toLowerCase().includes(q.toLowerCase())).slice(0, 10) : [];
    const zname = (id) => zones.find((z) => z.id === id)?.name || "…";
    return (
        <section>
            <PageHead title="Products limited to zones" sub="A product with zones listed is shown and sold only to addresses in those zones." />
            <label className="mt-3 flex items-center gap-2 rounded-xl bg-muted px-3 py-2 max-w-md"><MapPin size={15} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a product to limit" className="bg-transparent outline-none text-sm flex-1 min-w-0" /></label>
            {matches.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">{matches.map((p) => <button key={p.id} type="button" onClick={() => setEdit({ id: p.id, name: p.name, zone_ids: p.available_zone_ids || [] })} className="rounded-full border border-border px-3 py-1 text-xs font-semibold">{p.name}</button>)}</div>
            )}
            {!restricted && <Loading />}
            {restricted?.length === 0 && <Empty testid="lse-zone-products-empty">Every product is sold in every zone.</Empty>}
            <div className="mt-3 space-y-2">
                {(restricted || []).map((p) => (
                    <div key={p.id} className="bg-card border border-border rounded-2xl px-4 py-3 flex items-center gap-3">
                        <div className="flex-1 min-w-0"><p className="font-semibold text-sm truncate">{p.name}</p><p className="text-xs text-muted-foreground truncate">Only in: {p.available_zone_ids.map(zname).join(", ")}</p></div>
                        <Btn tone="ghost" className="!px-3" onClick={() => setEdit({ id: p.id, name: p.name, zone_ids: p.available_zone_ids })}>Edit</Btn>
                    </div>
                ))}
            </div>
            {edit && (
                <Modal title={`${edit.name} · zones`} onClose={() => setEdit(null)} footer={<><Btn tone="ghost" onClick={() => setEdit(null)}>Cancel</Btn><Btn onClick={save}>Save</Btn></>}>
                    <p className="text-xs text-muted-foreground">No zones selected = sold everywhere.</p>
                    <div className="mt-3 flex flex-wrap gap-2">{zones.map((z) => <Chip key={z.id} on={edit.zone_ids.includes(z.id)} onClick={() => setEdit({ ...edit, zone_ids: edit.zone_ids.includes(z.id) ? edit.zone_ids.filter((x) => x !== z.id) : [...edit.zone_ids, z.id] })}>{z.name}</Chip>)}</div>
                </Modal>
            )}
        </section>
    );
}

function Ratings() {
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try { const { data } = await api.post("/admin/lse/ratings/recompute"); toast.success(`${data.products_updated} product rating(s) refreshed`); }
        catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
    };
    return (
        <section>
            <PageHead title="Product ratings" sub="Each product's average rating and number of ratings are updated automatically when a customer reviews it. Refresh them all once after switching this on." />
            <Btn className="mt-3" tone="ghost" onClick={run} disabled={busy}><RefreshCcw size={15} /> Refresh all ratings</Btn>
        </section>
    );
}
